import { PATHS } from 'shared/consts/paths';

// Cluster keys used by ResourcesPage headers and by RelatedInCluster component
export type ClusterKey = 'validation' | 'bulk' | 'industry';

export interface ClusterMeta {
    key: ClusterKey;
    label: string;
    intro: string;
}

export const CLUSTERS: Record<ClusterKey, ClusterMeta> = {
    validation: {
        key: 'validation',
        label: 'Validation & Line-Type Detection',
        intro: 'Free tools and guides for checking a phone number: identify mobile, landline, or VOIP; look up the carrier; and confirm whether a number is real and reachable.',
    },
    bulk: {
        key: 'bulk',
        label: 'Bulk & Enterprise Solutions',
        intro: 'Clean, enrich, and validate large phone lists before SMS or call campaigns. Bulk uploads, scrubbing, enrichment, API guidance, and comparison content.',
    },
    industry: {
        key: 'industry',
        label: 'Vertical Use Cases',
        intro: 'How teams in specific industries use PhoneCheckr — from real estate wholesalers to insurance agents to recruiting and healthcare — to stay compliant and keep outreach efficient.',
    },
};

// One entry per SEO page. Label is the human-readable page name used in
// visible breadcrumbs and related-links lists. desc is the short blurb
// shown on the ResourcesPage hub cards. Kept in sync with the <title>
// and H1 shown on each page.
export interface SeoPageMeta {
    path: string;
    label: string;
    desc: string;
    cluster: ClusterKey;
}

export const SEO_PAGES: readonly SeoPageMeta[] = [
    // Validation & Line-Type Detection
    {
        path: PATHS.VOIP_CHECKER,
        label: 'VOIP Checker',
        desc: 'Check whether a number is VOIP before calling or texting it.',
        cluster: 'validation',
    },
    {
        path: PATHS.CARRIER_LOOKUP,
        label: 'Phone Carrier Lookup',
        desc: 'Identify the carrier behind any phone number: AT&T, Verizon, Google Voice, Twilio, and more.',
        cluster: 'validation',
    },
    {
        path: PATHS.FREE_PHONE_LOOKUP,
        label: 'Free Phone Number Lookup',
        desc: 'Look up any phone number for free: line type, carrier, and validity. No account required.',
        cluster: 'validation',
    },
    {
        path: PATHS.CHECK_MOBILE_OR_LANDLINE,
        label: 'Check Mobile or Landline',
        desc: 'Free tool to instantly classify any US or Canadian phone number by line type.',
        cluster: 'validation',
    },
    {
        path: PATHS.WHAT_IS_VOIP_NUMBER,
        label: 'What Is a VOIP Number?',
        desc: 'Plain-English guide to VOIP: what it is, common providers, and how to identify one.',
        cluster: 'validation',
    },
    {
        path: PATHS.WHAT_IS_LANDLINE,
        label: 'What Is a Landline Number?',
        desc: 'Definition of a landline, how it differs from mobile and VOIP, and why it matters for texting.',
        cluster: 'validation',
    },
    {
        path: PATHS.CAN_YOU_TEXT_LANDLINE,
        label: 'Can You Text a Landline?',
        desc: 'What actually happens when you send an SMS to a landline, and how to avoid silent failures.',
        cluster: 'validation',
    },
    {
        path: PATHS.IS_THIS_A_REAL_PHONE,
        label: 'Is This a Real Phone Number?',
        desc: 'How to tell if a number is real, assigned, and active versus fake, disconnected, or unassigned.',
        cluster: 'validation',
    },
    {
        path: PATHS.FREE_PHONE_VALIDATION,
        label: 'Free Phone Number Validation',
        desc: 'Free validation for individual phone numbers: line type, country, and format check.',
        cluster: 'validation',
    },
    {
        path: PATHS.FREE_PHONE_CHECKER,
        label: 'Free Phone Number Checker',
        desc: 'Free instant phone checker: line type, carrier, and validity — no account required.',
        cluster: 'validation',
    },
    {
        path: PATHS.PHONE_VERIFICATION_TOOL,
        label: 'Phone Number Verification Tool',
        desc: 'Verify individual phone numbers or upload a CSV for bulk verification.',
        cluster: 'validation',
    },

    // Bulk & Enterprise Solutions
    {
        path: PATHS.BULK_PHONE_VALIDATION,
        label: 'Bulk Phone Number Validation',
        desc: 'Upload a CSV and validate phone numbers in bulk at $0.01 per number. Line type, carrier, and validity.',
        cluster: 'bulk',
    },
    {
        path: PATHS.HOW_TO_CLEAN_SMS_LIST,
        label: 'How to Clean an SMS List',
        desc: 'Remove invalid, landline, and VOIP numbers from your list before your next campaign.',
        cluster: 'bulk',
    },
    {
        path: PATHS.HOW_TO_VERIFY_PHONE,
        label: 'How to Verify a Phone Number',
        desc: 'Step-by-step guide to verifying any US or Canadian phone number instantly.',
        cluster: 'bulk',
    },
    {
        path: PATHS.PHONE_SCRUBBER,
        label: 'Phone Number Scrubber',
        desc: 'Scrub a phone list to remove invalids, landlines, and VOIP numbers before outreach.',
        cluster: 'bulk',
    },
    {
        path: PATHS.PHONE_ENRICHMENT,
        label: 'Phone Number Enrichment',
        desc: 'Enrich phone records with carrier name, line type, and country data.',
        cluster: 'bulk',
    },
    {
        path: PATHS.PHONE_VALIDATION_API,
        label: 'Phone Number Validation API',
        desc: 'Guidance on when a validation API makes sense versus a CSV-first tool.',
        cluster: 'bulk',
    },
    {
        path: PATHS.US_AREA_CODES,
        label: 'US Area Codes',
        desc: 'Reference on US area codes, how they are assigned, and what they signal about a number.',
        cluster: 'bulk',
    },
    {
        path: PATHS.ALTERNATIVES,
        label: 'PhoneCheckr Alternatives',
        desc: 'Compare PhoneCheckr with Twilio Lookup, Numverify, AbstractAPI, and other validation tools.',
        cluster: 'bulk',
    },

    // Vertical Use Cases
    {
        path: PATHS.INSURANCE,
        label: 'Phone Validation for Insurance',
        desc: 'Clean insurance lead lists before calling or texting. Filter landlines, route mobiles.',
        cluster: 'industry',
    },
    {
        path: PATHS.REAL_ESTATE,
        label: 'Phone Validation for Real Estate',
        desc: 'Validate motivated-seller and homeowner phone lists before outreach.',
        cluster: 'industry',
    },
    {
        path: PATHS.ECOMMERCE,
        label: 'Phone Validation for E-Commerce',
        desc: 'Clean customer and lead phone lists to cut SMS spend and lift deliverability.',
        cluster: 'industry',
    },
    {
        path: PATHS.HEALTHCARE,
        label: 'Phone Validation for Healthcare',
        desc: 'Validate patient phone numbers before sending appointment or reminder SMS.',
        cluster: 'industry',
    },
    {
        path: PATHS.RECRUITING,
        label: 'Phone Validation for Recruiting',
        desc: 'Split candidate contact lists into text-ready mobiles and call-ready landlines.',
        cluster: 'industry',
    },
    {
        path: PATHS.DEBT_COLLECTION,
        label: 'Phone Validation for Debt Collection',
        desc: 'Confirm line type for every debtor number for TCPA-aware calling and texting.',
        cluster: 'industry',
    },
];

// Fast lookups
const PATH_TO_META = new Map<string, SeoPageMeta>(SEO_PAGES.map(p => [p.path, p]));

export const getSeoPageMeta = (path: string): SeoPageMeta | undefined => PATH_TO_META.get(path);

export const getClusterPeers = (currentPath: string, limit = 5): SeoPageMeta[] => {
    const meta = getSeoPageMeta(currentPath);

    if (!meta) return [];

    return SEO_PAGES.filter(p => p.cluster === meta.cluster && p.path !== currentPath).slice(0, limit);
};

export const getPagesInCluster = (cluster: ClusterKey): SeoPageMeta[] => SEO_PAGES.filter(p => p.cluster === cluster);
